"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSchema = exports.toFraction = exports.getUnboxingDate = exports.isFieldVarianted = exports.getNumVariants = exports.flattenObject = exports.getFabricContent = void 0;
const fabricAttributes_1 = require("./attributes/fabricAttributes");
const product_1 = require("./types/product");
const zipperSchema_1 = require("./schemas/zipperSchema");
const ribbingSchema_1 = require("./schemas/ribbingSchema");
const fabricSchema_1 = require("./schemas/fabricSchema");
const sewingPatternSchema_1 = require("./schemas/sewingPatternSchema");
const buttonProductSchema_1 = require("./schemas/buttonProductSchema");
const threadSchema_1 = require("./schemas/threadSchema");
const trimSchema_1 = require("./schemas/trimSchema");
const elasticSchema_1 = require("./schemas/elasticSchema");
const biasTapeSchema_1 = require("./schemas/biasTapeSchema");
const fractionUtils_1 = require("./fractionUtils");
const otherSupplySchema_1 = require("./schemas/otherSupplySchema");
/**
 * Get Fabric Content
 * @param content the fabric content
 * @param usePercentSign if set, adds percentage ('XX%') to known content. Defaults true
 * @param combineBlend if set, will combine blend i.e. "Cotton", "Blend" -> "Cotton Blend". Defaults true
 */
const getFabricContent = (content, params) => {
    var _a, _b, _c;
    const usePercentSign = (_a = params === null || params === void 0 ? void 0 : params.usePercentSign) !== null && _a !== void 0 ? _a : true;
    const combineBlend = (_b = params === null || params === void 0 ? void 0 : params.combineBlend) !== null && _b !== void 0 ? _b : true;
    const cType = content.contentType;
    if (cType === fabricAttributes_1.ContentType.Known && content.knownContent) {
        return content.knownContent.map((kcontent) => (usePercentSign ? `${kcontent.amount}% ` : '') + kcontent.content);
    }
    else if (cType === fabricAttributes_1.ContentType.EstimatedSpecific && ((_c = content.estimatedSpecificContent) === null || _c === void 0 ? void 0 : _c.primary)) {
        const secondary = content.estimatedSpecificContent.secondary;
        if (secondary && secondary === 'Blend' && combineBlend)
            return [content.estimatedSpecificContent.primary + ' Blend'];
        const fabricContent = [content.estimatedSpecificContent.primary];
        if (secondary)
            fabricContent.push(secondary);
        return fabricContent;
    }
    else if (cType === fabricAttributes_1.ContentType.EstimatedGeneral &&
        content.estimatedGeneralContent) {
        return [content.estimatedGeneralContent];
    }
    throw new Error(`Fabric content type (${content.contentType}) did not have a corresponding content value. Make sure to use validator`);
};
exports.getFabricContent = getFabricContent;
// Ref: https://medium.com/swlh/how-to-use-recursion-to-flatten-a-javascript-object-17e43cc00159
const flattenObject = (
// eslint-disable-next-line
oldObject, newArray = []) => {
    for (const key in oldObject) {
        const value = oldObject[key];
        if (value.constructor !== Object)
            newArray.push(value);
        else
            exports.flattenObject(value, newArray);
    }
    return newArray;
};
exports.flattenObject = flattenObject;
/**
 * Gets number of variants in fabric.
 * If the varianted fields are different lengths, returns greatest length.
 * If no varianted fields are specified, returns undefined
 * @param fabric fabric with variants
 * @returns number of variants
 */
const getNumVariants = (fabric) => {
    if (!fabric.variantedFields || fabric.variantedFields.length === 0)
        return undefined;
    const variantedFieldLengths = fabric.variantedFields.map((variantedField) => { var _a, _b; return (_b = (_a = fabric[variantedField]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0; });
    return Math.max(...variantedFieldLengths);
};
exports.getNumVariants = getNumVariants;
/**
 * Checks if a field is varianted
 * @param field field to check
 * @param fabric fabric
 * @returns true if field is varianted
 */
const isFieldVarianted = (field, variantedFields) => {
    if (!variantedFields)
        return false;
    return variantedFields.includes(field);
};
exports.isFieldVarianted = isFieldVarianted;
/**
 * Convert a Date instance to a string in MM-DD format
 * @param unboxingDate Date object of unboxing
 * @returns string in MM-DD format
 */
const getUnboxingDate = (unboxingDate) => {
    if (unboxingDate)
        return `${unboxingDate.toLocaleString('en-CA', {
            month: 'long'
        })} ${unboxingDate.getDate()}`;
    return '';
};
exports.getUnboxingDate = getUnboxingDate;
/**
 * convert decimal to fraction string
 * e.g. 2.5 -> '2 1/2'
 * @param x the number
 * @param useFractionChar display fraction as a character
 * @returns fraction string
 */
const toFraction = (val, useFractionChar = false) => {
    if (val % 1 >= 0.95)
        val = Math.round(val);
    const whole = Math.floor(val) > 0 ? `${Math.floor(val)} ` : '';
    const fraction = fractionUtils_1.getEstimatedFraction(val % 1);
    const fractionChar = fractionUtils_1.convertFractionToChar(fraction);
    return `${whole}${useFractionChar ? fractionChar : fraction}`.trim();
};
exports.toFraction = toFraction;
/**
 * Will return the correct schema given a productType
 * @param productType specifies the type of product
 * @returns ObjectSchemaOf<productType>
 */
const getSchema = (productType) => {
    switch (productType) {
        case product_1.ProductTypes.Zippers:
            return zipperSchema_1.ZipperSchema;
        case product_1.ProductTypes.Ribbing:
            return ribbingSchema_1.RibbingSchema;
        case product_1.ProductTypes.Fabric:
            return fabricSchema_1.FabricSchema;
        case product_1.ProductTypes['Sewing Patterns']:
            return sewingPatternSchema_1.SewingPatternSchema;
        case product_1.ProductTypes.Buttons:
            return buttonProductSchema_1.ButtonProductSchema;
        case product_1.ProductTypes.Thread:
            return threadSchema_1.ThreadSchema;
        case product_1.ProductTypes.Trim:
            return trimSchema_1.TrimSchema;
        case product_1.ProductTypes.Elastic:
            return elasticSchema_1.ElasticSchema;
        case product_1.ProductTypes['Bias Tape']:
            return biasTapeSchema_1.BiasTapeSchema;
        case product_1.ProductTypes['Other Supply']:
            return otherSupplySchema_1.OtherSupplySchema;
        default:
            // Need to return a schema if a productType doesn't have a schema defined yet
            return zipperSchema_1.ZipperSchema;
    }
};
exports.getSchema = getSchema;
//# sourceMappingURL=utils.js.map